import React, { useEffect, useRef } from "react";
import HelpSection from "../../../../components/dashboardcomponents/KnowledgeBase/KnowledgeBaseLangingPage/HelpSection";
import ArticleGrid from "../../../../components/dashboardcomponents/KnowledgeBase/KnowledgeBaseLangingPage/ArticleGrid";
import CategoryGrid from "../../../../components/dashboardcomponents/KnowledgeBase/KnowledgeBaseLangingPage/CategoryGrid";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTenantSlug } from "../../../../utils";
import { fetchfeatureArticlesExternal } from "../../../../store/features/getFeatureArticleExternal";
import { getAllExternalKbCategories } from "../../../../store/features/getExternalAllCategories";
import { fetchpopularArticlesExternal } from "../../../../store/features/getExternalPopularArticle";
import { saveSearchKeywordKbArticle } from "../../../../store/features/saveSearchKeywordKbArticle";
import { fetchArticlesExternal } from "../../../../store/features/getAllExternalArticles";

import BusinessNotFound from "../../../../components/dashboardcomponents/KnowledgeBase/KnowledgeBaseLangingPage/BusinessNotFound";
import { getKbGroupId } from "../../../../utils/kbGroupId";
// adjust path as needed

const KnowledgeBaseHome = () => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [tenantSlug, setTenantSlug] = React.useState('');
  const debounceRef = useRef(null);
  const keywordSavedRef = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const groupId = getKbGroupId();

  const { data: kbSettings, loading: kbSettingsLoading } = useSelector((state) => state.externalKbSettings);
  const { data: categoriesData, loading: categoriesLoading } = useSelector((state) => state.externalCatgroiesKb);
  const { articles: featuredArticles, loading: featuredLoading, error: featuredError } = useSelector((state) => state.featuredArticlesExternal);
  const { articles: popularArticlesData, loading: popularLoading } = useSelector((state) => state.popularArticlesExternal);
  const { articles: allArticles = [] } = useSelector((state) => state.allExternalArticles);

  useEffect(() => {
    const slug = getTenantSlug() || new URLSearchParams(window.location.search).get('tenant') || '';
    if (slug) setTenantSlug(slug);
  }, []);

  // Initial data load
  useEffect(() => {
    if (!tenantSlug || !groupId) return;
    dispatch(fetchpopularArticlesExternal({ businessName: tenantSlug, group_id: groupId }));
    dispatch(fetchfeatureArticlesExternal({ businessName: tenantSlug, group_id: groupId }));
    dispatch(getAllExternalKbCategories({ businessName: tenantSlug, group_id: groupId }));
    dispatch(fetchArticlesExternal({ businessName: tenantSlug, group_id: groupId }));
  }, [dispatch, tenantSlug, groupId]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);



  const suggestions = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const lower = searchQuery.toLowerCase();
    return allArticles
      .filter((a) => a.title?.toLowerCase().includes(lower))
      .slice(0, 8)
      .map((article) => ({
        label: article.title || '',
        sublabel: article.category || '',
        articleId: article._id,
        _raw: article,
      }));
  }, [searchQuery, allArticles]);

  const publicCategories = React.useMemo(
    () => (categoriesData?.data || []).filter((c) => c.visibility === 'public'),
    [categoriesData?.data]
  );

  const isSubdomain = () => {
    const hostname = window.location.hostname;
    return hostname !== 'localhost' && hostname !== '127.0.0.1' && hostname.split('.').length >= 3;
  };

  const navigateToArticle = (article) => {
    const id = article?._id;
    if (isSubdomain()) {
      navigate(`/${id}`);
    } else {
      navigate(`/kb/view/${id}?tenant=${tenantSlug}`, { state: { article, tenantSlug } });
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    const article = suggestion._raw;
    setSearchQuery(suggestion.label);
    if (isSubdomain()) {
      navigate(`/${article._id}`);
    } else {
      navigate(`/kb/view/${article._id}`, { state: { tenantSlug } })
    };
  };
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    keywordSavedRef.current = false; // reset on every new keystroke

    clearTimeout(debounceRef.current);
    if (value.trim() && tenantSlug) {
      debounceRef.current = setTimeout(() => {
        dispatch(saveSearchKeywordKbArticle({ businessName: tenantSlug, search: value.trim(), group_id: groupId }));
        keywordSavedRef.current = true; // mark saved after debounce fires
      }, 500);
    }
  };
  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    clearTimeout(debounceRef.current); // cancel pending debounce

    // Save only if debounce didn't already save it
    if (tenantSlug && !keywordSavedRef.current) {
      dispatch(saveSearchKeywordKbArticle({ businessName: tenantSlug, search: searchQuery.trim(), group_id: groupId }));
      keywordSavedRef.current = true;
    }

    if (isSubdomain()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate(`/kb/view/search?q=${encodeURIComponent(searchQuery.trim())}`, { state: { tenantSlug } });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleCategoryClick = (category) => {
    const slug = category.slug;
    if (isSubdomain()) {
      navigate(`/category/${slug}`);
    } else {
      navigate(`/kb/view/category/${slug}?tenant=${tenantSlug}`, { state: { category, tenantSlug } });
    }
  };




  if (featuredError === 'Business not found.') {
    return (
      <BusinessNotFound
        errorMessage={featuredError}
        navigate={navigate}
      />
    );
  }
  return (
    <>
      <HelpSection
        description='Get answers and explore new ways to use '
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearch}
        onKeyDown={handleKeyDown}
        productName={
          kbSettings?.data?.business_name
            ? kbSettings.data.business_name.charAt(0).toUpperCase() + kbSettings.data.business_name.slice(1)
            : 'Trustchat'
        }
        suggestions={suggestions}
        onSuggestionSelect={handleSuggestionSelect}
      />
      <ArticleGrid
        marginTop="90px"
        articles={featuredArticles}
        title="Feature Article"
        loading={featuredLoading}
        businessName={tenantSlug}
        showSeeMore={!featuredLoading}
        skeletonCount={featuredArticles?.length || 3}
      />
      <CategoryGrid
        categories={publicCategories}
        loading={categoriesLoading}
        onCategoryClick={handleCategoryClick}
      />
      <ArticleGrid
        articles={popularArticlesData}
        title="Popular Articles"
        loading={popularLoading}
        businessName={tenantSlug}
      />
    </>
  );
};

export default KnowledgeBaseHome;